import { trpc as appManagerTrpc } from '@tencent/tpro_rmonitor_manage_server_app_manage';
import { trpc } from '@tencent/tpro_rmonitor_user_manager_user_role_auth';
import moment from 'moment';
import { getUserId } from '@/infrastructure/auth';
import { TRPCGet, TRPCPost } from '@/infrastructure/axios';
import { API_PREFIX } from '@/infrastructure/axios/code';
import { logger } from '@/infrastructure/logger';
import { getProductId, getPid } from '@/infrastructure/product-info';
import { appManagerService } from '@/service/app-manager';
import { WithTypeCast } from '@raft/utils';

type IUser = WithTypeCast<trpc.rmonitor.user_manager.IUser>;
type IRole = WithTypeCast<trpc.rmonitor.user_manager.IRole>;
type IApplyCreateReq = WithTypeCast<trpc.rmonitor.user_manager.IApplyCreateReq>;
type IApplyCreateRsp = WithTypeCast<trpc.rmonitor.user_manager.IApplyCreateRsp>;

type IProduct = WithTypeCast<appManagerTrpc.rmonitor.manage_server.IProduct>;
type IGroup = WithTypeCast<appManagerTrpc.rmonitor.manage_server.IGroup>;
type IDepartment = WithTypeCast<appManagerTrpc.rmonitor.manage_server.IDepartment>;

export interface UserItem {
  createTime: string;
  department: string;
  id: number;
  name: string;
  roleId: number;
}

export interface Department {
  id: number,               // 部门id
  name: string,             // 部门名称
  level: number,            // 等级 1-BG 2-部门
  parentId: number,         // 隶属部门id
  parentName: string        // 隶属部门名称
  isGroup: number,          // 是否是子集团
}

export interface Group {
  groupId: string;
  groupName: string;
  bgName: string;
  owner: string;
  creator: string;
}

export type GroupProducts = {
  group: IGroup;
  products: IProduct[]
};

/**
 * 新的拉取所有group分组列表下的product产品
 */
export const getAllProductGroupsByUserV2 = async (): Promise<GroupProducts[]> => {
  try {
    const st = Date.now();
    const resp = await appManagerService.getProductGroupsByUserV2({
      username: getUserId(),
    });

    const groupData = resp?.groups ?? [];
    const products = resp?.products ?? [];
    const ret = groupData.map((item) => {
      const productData = products
        .sort((a: IProduct, b: IProduct) => ((a.productName ?? '')?.localeCompare(b.productName ?? '')));
      return {
        group: item,
        products: productData,
      };
    }).sort((a: GroupProducts, b: GroupProducts) => (
      (a.group.groupName ?? '').localeCompare(b.group.groupName ?? '')
    ));
    logger.info('getProductGroupsByUser|perf cost:', Date.now() - st);
    return ret;
  } catch (e) {
    logger.error('getProductGroupsByUser err: ', e);
    return [];
  }
};

/**
 * 获取部门列表
 */
export const getDepartments = async (): Promise<IDepartment[]> => {
  let department: IDepartment[] = [];
  try {
    const resp = await appManagerService.getDepartments({});
    department = resp;
  } catch (e) {
    logger.error('getDepartments err: ', e);
  }

  return department;
};

/**
 * 注册分组
 * @param groupName
 * @param bgName
 * @param owner
 */
export const groupRegister = async (
  groupName: string,
  bgName: string,
  owner: string,
): Promise<string> => {
  let groupId = '';
  try {
    const resp = await appManagerService.projectGroupRegister({
      groupName,
      bgName,
      owner,
      creator: getUserId(),
    });
    groupId = resp;
  } catch (e) {
    logger.error('groupRegister err: ', e);
  }

  return groupId;
};

/**
 * 注册平台
 * @param platform
 * @param productName
 * @param bundleId
 * @param currentGroupId
 */
export const productRegister = async (
  platform: string,
  productName: string,
  bundleId: string,
  currentGroupId?: string,
): Promise<{ productId: string, appKey: string }> => {
  const ret = { productId: '', appKey: '' };
  try {
    const resp = await appManagerService.productRegister({
      product: {
        platform: platform.toLowerCase(),
        productName,
        processName: bundleId,
        groupId: currentGroupId,
      },
      creator: getUserId(),
    });
    ret.productId = resp.productId ?? '';
    ret.appKey = resp.appKey ?? '';
  } catch (e) {
    logger.error('productRegister err: ', e);
  }

  return ret;
};

/**
 * 申请权限
 */
export const applyCreate = async (req: IApplyCreateReq) => {
  try {
    const resp: IApplyCreateRsp = await TRPCPost(
      `/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/ApplyCreate`,
      {
        productId: getProductId(),
        applyer: getUserId(),
        platformId: getPid(),
        ...req,
      },
      axiosRequestConfig(),
    );
    return resp.baseRsp;
  } catch (e) {
    logger.error('applyCreate err: ', e);
    return null;
  }
};

const axiosRequestConfig = () => ({
  headers: {
    'Content-Type': 'application/json',
    'Server-Type': 'trpc',
    staffName: getUserId(),
    tenantKey: getProductId(),
  },
});

// 获取权限列表
export const getAllRoles = async (): Promise<IRole[]> => {
  try {
    const resp = await TRPCGet(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetAllRoles?productId=${getProductId()}`, axiosRequestConfig());
    const data: IRole[] = resp.roles;
    return data;
  } catch (e) {
    logger.error('getAllRoles err', e);
    return [];
  }
};

// 获取用户列表
export const getAllUsers = async (): Promise<UserItem[]> => {
  try {
    const resp = await TRPCPost(`/${API_PREFIX}/trpc/trpc.rmonitor.user_manager.UserRoleAuth/GetAllUsers`, {
      productId: getProductId(),
    }, axiosRequestConfig());
    const data = resp.users;
    return data.map((item: IUser) => ({
      createTime: moment(item.createTime, 'X').format('YYYY-MM-DD'),
      department: item.department,
      id: item.id,
      name: item.name,
      roleId: item.roleId,
    }));
  } catch (e) {
    logger.error('getAllUsers err', e);
    return [];
  }
};
